import { StateCreator } from 'zustand';
import { IVisitNotesTemplatesFields, IVisitNotesTemplatesSlice } from './types';
import { institutionsApi } from '@/API';
import { convertKeys } from '@/utils/common';

const initState: IVisitNotesTemplatesFields = {
  draftVisitNotesTemplate: null,
  visitNotesTemplates: [],
  loadingVisitNotesTemplates: false,
};

export const visitNotesTemplatesSlice: StateCreator<
  IVisitNotesTemplatesSlice
> = (set, get) => ({
  ...initState,

  setDraftVisitNotesTemplate: (draftVisitNotesTemplate) =>
    set({ draftVisitNotesTemplate }),

  getVisitNoteTemplateById: (id: string) =>
    get().visitNotesTemplates.find((vnt) => vnt.id === id) ?? null,

  fetchVisitNotesTemplates: async (institutionId) => {
    set({ loadingVisitNotesTemplates: true });

    try {
      const response =
        await institutionsApi.getVisitNotesTemplates(institutionId);
      const templates = convertKeys(response.data.data);
      set({ visitNotesTemplates: templates });
    } catch (error) {
      console.error('Fetching visit notes templates failed:', error);
      set({ visitNotesTemplates: [] });
    } finally {
      set({ loadingVisitNotesTemplates: false });
    }
  },

  createVisitNotesTemplate: async (institutionId, template) => {
    try {
      const response = await institutionsApi.createVisitNotesTemplate(
        institutionId,
        convertKeys(template, 'snake'),
      );
      const createdTemplate = convertKeys(response.data.data);

      set((state) => ({
        visitNotesTemplates: [...state.visitNotesTemplates, createdTemplate],
      }));
    } catch (error) {
      console.error('Creating visit notes template failed:', error);
      throw error;
    }
  },

  updateVisitNotesTemplate: async (id, template) => {
    try {
      const response = await institutionsApi.updateVisitNotesTemplate(
        id,
        convertKeys(template, 'snake'),
      );
      const updatedTemplate = convertKeys(response.data.data);

      set((state) => ({
        visitNotesTemplates: state.visitNotesTemplates.map((t) =>
          t.id === id ? updatedTemplate : t,
        ),
      }));
    } catch (error) {
      console.error('Updating visit notes template failed:', error);
      throw error;
    }
  },

  deleteVisitNotesTemplate: async (id) => {
    try {
      await institutionsApi.deleteVisitNotesTemplate(id);

      set((state) => ({
        visitNotesTemplates: state.visitNotesTemplates.filter(
          (t) => t.id !== id,
        ),
      }));
    } catch (error) {
      console.error('Deleting visit notes template failed:', error);
      throw error;
    }
  },

  clearVisitNotesTemplates: () => {
    set(initState);
  },
});
